import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut, ShoppingCart, LayoutDashboard, FileText, Clock, MessageSquare, DollarSign, User as UserIcon, Coins, Database, CreditCard, TrendingUp, Newspaper } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import NewOrderWithPricing from './NewOrderWithPricing';
import UserOrders from './UserOrders';
import LanguageSwitcher from './LanguageSwitcher';
import TicketsList from './TicketsList';
import UserDashboard from './UserDashboard';
import RechargeAccount from './RechargeAccount';
import UserTransactions from './UserTransactions';
import FileHistory from './FileHistory';
import MyProfile from './MyProfile';
import QueueBadge from './QueueBadge';
import SupportIcons from './SupportFab';
import NewsBell from './NewsBell';
import NewsPage from './NewsPage';
import WelcomeBonusModal from './WelcomeBonusModal';
import EVCDashboard from './EVCDashboard';
import OriginalFilesPage from './OriginalFilesPage';
import useUnreadTracker from '../hooks/useUnreadTracker';
import useBadges from '../hooks/useBadges';
import useDocumentTitle, { PAGE_TITLES } from '../hooks/useDocumentTitle';
import { UnreadBadgeProvider } from '../context/UnreadBadgeContext';

import AuthedAvatarImage from './AuthedAvatarImage';
import ServerClock from './ServerClock';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const NOTIF_KEY = 'autoremap.readyNotified.v2';
const LAST_SEEN_KEY = 'autoremap.lastSeen.v2';
const loadNotified = () => {
  try { return new Set(JSON.parse(localStorage.getItem(NOTIF_KEY) || '[]')); }
  catch { return new Set(); }
};
const saveNotified = (set) => {
  try { localStorage.setItem(NOTIF_KEY, JSON.stringify([...set].slice(-200))); } catch (_) { /* silent */ }
};
const loadLastSeen = () => {
  const v = parseInt(localStorage.getItem(LAST_SEEN_KEY) || '0', 10);
  return Number.isFinite(v) ? v : 0;
};
const saveLastSeen = (ms) => {
  try { localStorage.setItem(LAST_SEEN_KEY, String(ms)); } catch (_) { /* silent */ }
};

export default function UserPanel({ user, token, onLogout, refreshUser }) {
  // Translation hook for the menu items, page titles, and shared
  // customer UI labels. `nav.*` keys live in `common.json`. Whole
  // file uses ONE hook + a tiny `tNav` shortcut to keep the JSX read-
  // able instead of writing `t('common:nav.dashboard')` everywhere.
  const { t: tCommon } = useTranslation('common');
  const tNav = (k) => tCommon(`nav.${k}`);
  // On first mount, if we landed back from Stripe (success_url contains
  // `rp_sid`), deep-link the user straight to the Recharge page. The
  // RechargeAccount component picks up the same query param and polls
  // /api/stripe/status to credit the balance.
  const stripeReturnSid = (() => {
    try { return new URLSearchParams(window.location.search).get('rp_sid'); }
    catch { return null; }
  })();
  const [activePage, setActivePage] = useState(stripeReturnSid ? 'recharge' : 'dashboard');
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [readyTotal, setReadyTotal] = useState(0);
  // All nav/header counters in ONE request + live `badges:changed` push.
  const { badges, version: badgesVersion } = useBadges({ token, isAdmin: false });
  // Drive the browser tab title. Show unread-tickets count on the
  // Tickets page; show ready-file count on My Orders so the user
  // can park the tab in the background and notice work coming back.
  useDocumentTitle(
    PAGE_TITLES[activePage] || 'AUTO-REMAP',
    {
      count:
        activePage === 'tickets' ? unreadTotal :
        activePage === 'orders'  ? readyTotal  : 0,
    },
  );
  // Optional initial status filter when admin lands on My Orders via a dashboard tile click
  const [ordersInitialStatus, setOrdersInitialStatus] = useState('');
  // Deep-link target — when a toast or transaction row asks to open a
  // specific order, we switch to "My Orders" and prime the table to
  // open that drawer immediately.
  const [ordersInitialOpenId, setOrdersInitialOpenId] = useState('');
  // Edit-order modal state. When set, a full-screen overlay with the wizard
  // pre-filled with this order's data is shown.
  const [editingOrder, setEditingOrder] = useState(null);
  const notifiedRef = useRef(loadNotified());
  const lastSeenRef = useRef(loadLastSeen());

  // Cross-component "open this order" requests — fired by the
  // ready-to-download toast's "Open" button. Switches to My Orders
  // and primes the deep-link.
  useEffect(() => {
    const handler = (e) => {
      const orderId = e?.detail?.orderId;
      if (!orderId) return;
      setOrdersInitialOpenId(orderId);
      setActivePage('orders');
    };
    window.addEventListener('user:open-order', handler);
    return () => window.removeEventListener('user:open-order', handler);
  }, []);

  // Deep-link support: emails carry links like `?page=recharge`,
  // `?page=orders&orderId=<id>`, `?page=tickets&ticketId=<id>` so
  // clicking a CTA in an email lands the user on the right section.
  // We ALSO keep `activePage` synced with the URL going forward, so
  // (a) the address bar always reflects the current section (users can
  // copy/share deep links), (b) browser back/forward buttons work, and
  // (c) refreshing the page keeps the user on the same section.
  const ALLOWED_PAGES = React.useMemo(() => (
    ['dashboard', 'new-order', 'original-files', 'orders', 'history', 'transactions', 'tickets', 'evc', 'chip-tuning', 'recharge', 'profile']
  ), []);

  // Read `?page=` on first mount + prime order drawer via `?orderId=`.
  // Keep the params in the URL so a manual refresh restores the section.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const page = (params.get('page') || '').toLowerCase();
    const orderId = params.get('orderId') || '';
    const ticketId = params.get('ticketId') || '';
    if (ALLOWED_PAGES.includes(page)) {
      setActivePage(page);
    }
    if (orderId && (page === 'orders' || !page)) {
      setOrdersInitialOpenId(orderId);
      setActivePage('orders');
    }
    if (ticketId) {
      // TicketsList reads `?ticketId=` on its own mount to auto-open
      // the ticket. We just need to be on the page.
      setActivePage('tickets');
    }
  }, [ALLOWED_PAGES]);

  // Keep the URL synced with activePage — every time the user
  // navigates via the sidebar, `history.pushState` writes
  // `?page=<current>` so:
  //   - address bar reflects the section
  //   - copy-paste + bookmarks work
  //   - browser back/forward walks the section history
  // We DON'T push on the very first render (would create an empty
  // history entry), only on subsequent changes.
  const firstUrlSyncRef = React.useRef(true);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (firstUrlSyncRef.current) {
      firstUrlSyncRef.current = false;
      return;
    }
    const url = new URL(window.location.href);
    if (activePage && activePage !== 'dashboard') {
      url.searchParams.set('page', activePage);
    } else {
      url.searchParams.delete('page');
    }
    // Drop transient deep-link params — they only make sense on
    // the initial landing click from an email.
    url.searchParams.delete('orderId');
    url.searchParams.delete('ticketId');
    // Use pushState so back/forward walks section history.
    window.history.pushState({ page: activePage }, '', url.toString());
  }, [activePage]);

  // Wire browser back/forward buttons — they emit `popstate`, and
  // we re-read `?page=` from the URL to sync `activePage`.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => {
      const params = new URLSearchParams(window.location.search);
      const page = (params.get('page') || 'dashboard').toLowerCase();
      if (ALLOWED_PAGES.includes(page)) {
        firstUrlSyncRef.current = true; // skip re-pushing state
        setActivePage(page);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [ALLOWED_PAGES]);


  // Live EVC credit balance shown in the top header — only meaningful for
  // users who have linked their EVC ID. We re-fetch on mount, periodically,
  // and on demand after a successful EVC purchase.
  const [evcBalance, setEvcBalance] = useState(null);
  const refreshEvcBalance = useCallback(async () => {
    if (!user?.evc_customer_id) { setEvcBalance(null); return; }
    try {
      const r = await fetch(`${API_URL}/api/evc/customer/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const j = await r.json();
      const b = j?.evc_balance;
      setEvcBalance(b === null || b === undefined ? null : Number(b));
    } catch (_) { /* silent */ }
  }, [token, user?.evc_customer_id]);

  useEffect(() => {
    refreshEvcBalance();
    const id = setInterval(refreshEvcBalance, 300000);
    return () => clearInterval(id);
  }, [refreshEvcBalance]);

  // News unread counter — powers the badge on the new "News" nav item.
  // Value comes from the unified badge payload (one request for all
  // counters) and is refreshed by the socket push / unread bus.
  const newsUnread = badges?.news_unread ?? 0;

  // Wrapper that lets UserDashboard navigate to My Orders with a pre-applied status filter.
  const openOrders = useCallback((statusFilter = '') => {
    setOrdersInitialStatus(statusFilter || '');
    setActivePage('orders');
  }, []);

  // Menu click: wipe any pre-applied status filter so clicking "My Orders"
  // in the top nav always shows the unfiltered list. The filter only
  // lingers when the user arrives from a Dashboard stat tile via openOrders().
  const navigateFromMenu = useCallback((page) => {
    setOrdersInitialStatus('');
    setActivePage(page);
  }, []);

  // Ticket unread count — part of the unified badge payload.
  const unreadTotalFromBadges = badges?.tickets_unread ?? 0;
  useEffect(() => { setUnreadTotal(unreadTotalFromBadges); }, [unreadTotalFromBadges]);

  // ---------- Ready-file toast notifications ----------
  const downloadModified = useCallback(async (orderId, name) => {
    try {
      const r = await fetch(`${API_URL}/api/orders/${orderId}/file/modified`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { toast.error('Download failed'); return; }
      const blob = await r.blob();
      const a = document.createElement('a');
      const url = URL.createObjectURL(blob);
      a.href = url; a.download = name || 'modified-file.bin';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (_) { toast.error('Download failed'); }
  }, [token]);

  const checkReadyOrders = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/orders/?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const orders = await r.json();
      const now = Date.now();
      const lastSeen = lastSeenRef.current || 0;
      let readyCount = 0;

      for (const o of orders) {
        if (o.status === 'cancelled') continue;
        // Determine when the file becomes downloadable to the CUSTOMER:
        // it must be both uploaded AND the order must be "released" — i.e.
        // either marked completed/closed by the engineer, or admin set a
        // custom ready_at that has elapsed. Just having modified file
        // uploaded while status is still working/open/waiting is NOT
        // enough — must match the backend rule in download_modified_file.
        const rt = o.ready_at ? new Date(o.ready_at).getTime() : 0;
        const mt = o.modified_uploaded_at ? new Date(o.modified_uploaded_at).getTime() : 0;
        const isCompletedStatus = o.status === 'completed' || o.status === 'closed';
        const rtPassed = rt && rt <= now;
        let readyAt = 0;
        if (mt && (isCompletedStatus || rtPassed)) {
          // Use whichever signal "released" the file — ready_at if it's
          // the gate, else the moment the engineer flipped status to
          // completed (we approximate via modified_uploaded_at since
          // that's what the customer actually got).
          readyAt = rtPassed ? rt : mt;
        }
        if (!readyAt) continue;
        // Tally "ready to download" globally — used by the live indicator.
        // We exclude orders whose modified file the customer already downloaded
        // to keep the badge actionable.
        if ((o.modified_download_count || 0) === 0) readyCount += 1;
        // First-ever visit: only toast for files newer than 24h to avoid old-data spam
        const cutoff = lastSeen || (now - 24 * 3600 * 1000);
        if (readyAt < cutoff) continue;

        const sig = `${o.id}:${readyAt}`;
        if (notifiedRef.current.has(sig)) continue;
        notifiedRef.current.add(sig);

        const label = `Order ${o.order_number || o.id.slice(0, 8)}`;
        const vehicle = [o.vehicle_brand, o.vehicle_model].filter(Boolean).join(' ') || 'your file';
        const fileName = o.modified_file_name || `${label}-modified.bin`;
        toast.success(`${label} is ready`, {
          description: `${vehicle} — modified file is ready to download`,
          duration: 12000,
          action: {
            label: 'Open',
            onClick: () => {
              // Same path the customer profile transactions use to deep-link
              // straight into a specific order's drawer.
              try { window.dispatchEvent(new CustomEvent('user:open-order', { detail: { orderId: o.id } })); }
              catch (_) { /* noop */ }
            },
          },
          // Sonner only renders one action button; "Download" is still
          // exposed via a cancel-style secondary so the user retains the
          // one-click download if they don't want to open the drawer.
          cancel: {
            label: 'Download',
            onClick: () => downloadModified(o.id, fileName),
          },
        });
      }
      setReadyTotal(readyCount);
      saveNotified(notifiedRef.current);
      lastSeenRef.current = now;
      saveLastSeen(now);
    } catch (_) { /* silent */ }
  }, [token, downloadModified]);

  // The "file is ready" check used to poll /api/orders/ every 60 s from
  // every open tab. It now runs on boot and whenever the server pushes a
  // `badges:changed` (order status / file delivery), with a 10-minute
  // timer left as a last-resort safety net.
  useEffect(() => {
    checkReadyOrders();
    const id = setInterval(checkReadyOrders, 600000);
    return () => clearInterval(id);
  }, [checkReadyOrders, badgesVersion]);

  // Granular per-item unread badges for Orders & Transactions. Mirrors
  // the admin panel — a Set of "seen" IDs in localStorage; the badge
  // shows the diff against the latest server-side ID list. The lists
  // arrive with the unified `/api/badges/summary` payload, so these
  // trackers make no network calls of their own.
  const badgesRef = React.useRef(null);
  badgesRef.current = badges;
  const ordersFetcher = useCallback(async () => (
    badgesRef.current ? badgesRef.current.order_ids || [] : null
  ), []);
  const txFetcher = useCallback(async () => (
    badgesRef.current ? badgesRef.current.transaction_ids || [] : null
  ), []);
  const ordersTracker = useUnreadTracker({
    storageKey: `auto-remap.user.${user?.id || 'anon'}.unread.orders`,
    fetcher: ordersFetcher,
    enabled: !!token,
    intervalMs: 0,
    refreshSignal: badgesVersion,
  });
  const txTracker = useUnreadTracker({
    storageKey: `auto-remap.user.${user?.id || 'anon'}.unread.transactions`,
    fetcher: txFetcher,
    enabled: !!token,
    intervalMs: 0,
    refreshSignal: badgesVersion,
  });

  // Same UX rule as on the admin side: opening the Transactions section
  // is treated as "I've seen them all" because the customer typically
  // skims the whole ledger at once. Orders/Tickets keep per-item
  // decrement (each row is an actionable work item).
  useEffect(() => {
    if (activePage !== 'transactions') return;
    if (txTracker.unreadIds.length === 0) return;
    txTracker.markManySeen(txTracker.unreadIds);
  }, [activePage, txTracker]);

  // Menu labels are now translated via the `common.nav.*` namespace.
  // The component must already call `useTranslation` in scope — we add
  // the hook below in the function-signature edit. Sub-labels are
  // short marketing strings; they stay English for now (small, not
  // critical for first localisation pass).
  const menuItems = [
    { id: 'dashboard',      label: tNav('dashboard'),      icon: LayoutDashboard },
    { id: 'new-order',      label: tNav('new_order'),      subLabel: tCommon('nav_sub.get_instant_price', { defaultValue: 'Get instant price' }), icon: ShoppingCart },
    // Top-line label intentionally short ("Original") — the long
    // descriptor lives in subLabel ("File Database"). Same trick is
    // applied across all locales in common.json so the menu fits at
    // narrower breakpoints. See Phase H16 changelog for rationale.
    { id: 'original-files', label: tNav('original_files'), subLabel: tCommon('nav_sub.database', { defaultValue: 'File Database' }), icon: Database },
    { id: 'orders',         label: tNav('my_orders'),      icon: FileText, badge: ordersTracker.unreadCount },
    { id: 'history',        label: tCommon('nav.file_history', { defaultValue: 'File History' }),         icon: Clock },
    { id: 'transactions',   label: tNav('transactions'),   icon: DollarSign, badge: txTracker.unreadCount },
    { id: 'tickets',        label: tNav('tickets'),        icon: MessageSquare, badge: unreadTotal },
    // EVC entry: top line emphasises the recognisable product name
    // ("WinOLS") because that's what shops know; sub-line clarifies
    // these are EVC-system credits. Swapped from the original
    // "EVC / WinOLS credits" wording in Phase H16.
    { id: 'evc',            label: 'WinOLS', subLabel: tCommon('nav_sub.evc_credits', { defaultValue: 'EVC credits' }), icon: Coins },
    { id: 'chip-tuning',    label: 'Tuning', subLabel: tCommon('nav_sub.catalog', { defaultValue: 'catalog' }), icon: TrendingUp,
      href: 'https://auto-remap.com/catalog/cars', external: true },
    // "Profile" instead of "My Profile" — shorter, same meaning;
    // applied to all locales (Mi Perfil → Perfil, Il Mio Profilo →
    // Profilo, Mein Profil → Profil, etc.).
    { id: 'profile',        label: tNav('profile'),        icon: UserIcon },
    { id: 'recharge',       label: tNav('recharge'),       subLabel: tCommon('nav_sub.top_up_balance', { defaultValue: 'Top up balance' }), icon: CreditCard },
    { id: 'news',           label: tNav('news'),           subLabel: tCommon('nav_sub.updates', { defaultValue: 'Updates & offers' }), icon: Newspaper, badge: newsUnread, highlightWhenUnread: true },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header — responsive. On mobile we collapse the customer name + live
          status pill, keep only logo + balance + burger; desktop keeps the
          full richer layout. */}
      <div className="sticky top-0 z-50 bg-gradient-to-r from-cyan-600 to-blue-700 text-white shadow-lg">
        <div className="max-w-[97rem] mx-auto px-3 sm:px-6 py-2.5 sm:py-4">
          <div className="flex justify-between items-center gap-2 min-w-0">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 overflow-hidden">
              <img
                src="/logo.png"
                alt="AUTO-REMAP"
                width="479"
                height="102"
                className="h-7 sm:h-11 w-auto max-w-[56px] sm:max-w-[200px] rounded-lg bg-white px-1 sm:px-2 py-1 shadow-md object-contain min-w-0"
                data-testid="user-header-logo"
              />
              {/* Customer name pill removed — the customer's own name was
                  redundant in their own header (they already see it on
                  the profile page + avatar). Removing it also gives the
                  Live-status pill and Logout button more horizontal
                  room so the logout label fits on a single line. */}
              {(() => {
                const hasReady = readyTotal > 0;
                const hasUnread = unreadTotal > 0;
                let label, dotClass, ring;
                if (hasUnread) {
                  label = unreadTotal === 1
                    ? tCommon('live_status.new_message_one', { count: unreadTotal, defaultValue: `${unreadTotal} new message` })
                    : tCommon('live_status.new_message_many', { count: unreadTotal, defaultValue: `${unreadTotal} new messages` });
                  dotClass = 'bg-red-400';
                  ring = 'bg-red-500/30 hover:bg-red-500/40';
                } else if (hasReady) {
                  label = tCommon('live_status.ready_to_download', { count: readyTotal, defaultValue: `${readyTotal} ready to download` });
                  dotClass = 'bg-emerald-400';
                  ring = 'bg-emerald-500/30 hover:bg-emerald-500/40';
                } else {
                  label = tCommon('live_status.no_pending', { defaultValue: 'No pending files' });
                  dotClass = 'bg-emerald-400';
                  ring = 'bg-white/15 hover:bg-white/25';
                }
                return (
                  <button
                    onClick={() => navigateFromMenu(hasUnread ? 'tickets' : 'orders')}
                    data-testid="live-status-pill"
                    title={hasUnread ? 'Unread messages — click to open Tickets' : (hasReady ? 'Files ready to download — click to open My Orders' : 'All caught up')}
                    className={`hidden lg:inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold transition-all ${ring}`}
                  >
                    <span className="relative inline-flex">
                      <span className={`w-2 h-2 rounded-full ${dotClass}`} />
                      {(hasReady || hasUnread) && (
                        <span className={`absolute inset-0 w-2 h-2 rounded-full ${dotClass} animate-ping opacity-75`} />
                      )}
                    </span>
                    Live · {label}
                  </button>
                );
              })()}
              {/* Server clock — live GMT+3 pill (spec §"Три варианта",
                  variant 1a). Placed AFTER the Live-messages pill and
                  BEFORE the flex spacer. */}
              <ServerClock />
            </div>
            <div className="flex items-center gap-1.5 sm:gap-4 md:gap-6 flex-shrink-0">
              <QueueBadge token={token} count={badges?.queue_count} managed className="hidden lg:flex" />
              <div className="hidden sm:flex items-center gap-3">
                <SupportIcons />
              </div>
              <NewsBell token={token} unreadCount={badges?.news_unread} managed />
              {/* Balance — clickable: navigates to Recharge page */}
              <button
                onClick={() => navigateFromMenu('recharge')}
                className="text-right leading-tight hover:opacity-80 transition flex-shrink-0"
                data-testid="user-balance"
                title="Click to top up"
              >
                <p className="hidden sm:block text-[10px] sm:text-sm opacity-90 leading-none whitespace-nowrap">{tCommon('labels.balance')}</p>
                <p className="text-xs sm:text-xl font-bold font-mono tabular-nums leading-tight whitespace-nowrap">€{user.balance?.toFixed(2) || '0.00'}</p>
              </button>
              {/* Mobile-only compact EVC chip — desktop has its own block below */}
              {user?.evc_customer_id && evcBalance !== null && (
                <button
                  onClick={() => navigateFromMenu('evc')}
                  className="sm:hidden flex items-center gap-0.5 px-1.5 py-1 rounded-full bg-emerald-500/25 border border-emerald-300/40 text-emerald-50 flex-shrink-0"
                  data-testid="user-evc-balance-mobile"
                  title="EVC credits"
                >
                  <Coins className="w-3 h-3" />
                  <span className="text-[11px] font-bold font-mono tabular-nums">{evcBalance}</span>
                </button>
              )}
              <div className="hidden sm:block text-right leading-tight" data-testid="user-reserved">
                <p className="text-xs sm:text-sm opacity-90">{tCommon('labels.reserved', { defaultValue: 'Reserved' })}</p>
                <p className="text-sm sm:text-lg font-medium text-amber-200 font-mono tabular-nums">€{user.reserved?.toFixed(2) || '0.00'}</p>
              </div>
              {/* EVC credits — same plain "label-on-top, number-below" style as
                  Balance/Reserved. Only visible for users with a linked EVC ID.
                  Click navigates to the EVC store. */}
              {user?.evc_customer_id && evcBalance !== null && (
                <button
                  onClick={() => navigateFromMenu('evc')}
                  className="hidden sm:block text-right leading-tight hover:opacity-80 transition"
                  data-testid="user-evc-balance"
                  title="EVC credits — click to open store"
                >
                  <p className="text-xs sm:text-sm opacity-90">EVC</p>
                  <p className="text-sm sm:text-lg font-medium text-emerald-200 font-mono tabular-nums">{evcBalance}</p>
                </button>
              )}
              <button
                onClick={onLogout}
                className="flex items-center gap-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-xs sm:text-sm whitespace-nowrap"
                data-testid="user-logout-btn"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">{tCommon('buttons.logout')}</span>
              </button>
              {/* Language selector — customer-only. Hidden on the admin
                  panel by product decision (admin panel is EN-only).
                  Shown on all screen sizes (mobile users need to switch
                  language just as much as desktop users do). */}
              <LanguageSwitcher compact />
              {/* Profile photo (Phase C). Falls back to initials when
                  the user hasn't uploaded a photo yet. */}
              <button
                onClick={() => navigateFromMenu('profile')}
                title={user.email}
                data-testid="user-header-avatar"
                className="ml-1 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/15 hover:bg-white/30 ring-2 ring-white/40 overflow-hidden flex items-center justify-center text-white text-xs font-bold transition-colors flex-shrink-0"
              >
                {user.avatar_url ? (
                  <AuthedAvatarImage
                    token={token}
                    src={user.avatar_url}
                    alt="Me"
                    className="w-full h-full object-cover"
                    fallback={<span>{((user.first_name?.[0] || '') + (user.surname?.[0] || user.email?.[0] || '?')).toUpperCase()}</span>}
                    testid="user-header-avatar-img"
                  />
                ) : (
                  <span>{((user.first_name?.[0] || '') + (user.surname?.[0] || user.email?.[0] || '?')).toUpperCase()}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Primary navigation — horizontal scroll tabs on every size. No
          hamburger. Wrapping `.edge-fade-x` gives a soft fade hint at the
          edges so users see there's more to scroll on small screens. */}
      <div className="sticky top-[58px] sm:top-[80px] z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[97rem] mx-auto px-3 sm:px-6">
          <div className="edge-fade-x">
            <div className="flex gap-1 py-2 overflow-x-auto no-scrollbar snap-x snap-mandatory">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = activePage === item.id;
                // Beacon highlight — e.g. News with unread posts: amber pill
                // + pulsing dot on the icon. Clears itself as soon as the
                // unread counter drops to 0 (i.e. after the customer reads).
                const flagged = !active && item.highlightWhenUnread && item.badge > 0;
                // External links (catalog, marketing site, …) render as
                // real <a target="_blank"> so right-click "Open in new
                // tab" and middle-click work as users expect. Internal
                // items keep the existing button + navigateFromMenu flow.
                const commonInner = (
                  <>
                    <span className="relative inline-flex flex-shrink-0">
                      <Icon className="w-4 h-4 lg:w-[18px] lg:h-[18px]" />
                      {flagged && (
                        <>
                          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500" />
                          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-400 animate-ping" />
                        </>
                      )}
                    </span>
                    <span className="flex flex-col items-start leading-tight text-left">
                      <span>{item.label}</span>
                      {item.subLabel && (
                        <span className={`hidden lg:inline text-[10px] font-normal ${active ? 'opacity-90' : 'text-slate-400'}`}>
                          {item.subLabel}
                        </span>
                      )}
                    </span>
                    {item.badge > 0 && (
                      <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${active ? 'bg-white text-cyan-700' : 'bg-red-500 text-white animate-pulse'}`}>
                        {item.badge}
                      </span>
                    )}
                  </>
                );
                const cls = `relative snap-start flex items-center gap-1.5 px-2.5 lg:px-3 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm flex-shrink-0 ${
                  active
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-sm'
                    : flagged
                      ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-300 hover:bg-amber-200 shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100'
                }`;
                if (item.external && item.href) {
                  return (
                    <a
                      key={item.id}
                      data-testid={`user-nav-${item.id}`}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cls}
                    >
                      {commonInner}
                    </a>
                  );
                }
                return (
                  <button
                    key={item.id}
                    data-testid={`user-nav-${item.id}`}
                    onClick={() => navigateFromMenu(item.id)}
                    className={cls}
                  >
                    {commonInner}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div>
        <UnreadBadgeProvider value={{
          // Customer-side markOrderSeen also clears the `:completed`
          // status-variant key. The backend feeds completed orders as
          // `{id}:completed` so a brand-new "ready for download" ping
          // surfaces in the badge — clicking the row should wipe both
          // the bare id and the status-suffixed twin so the badge
          // decrements correctly regardless of when the customer
          // opens it.
          markOrderSeen: (id) => {
            if (!id) return;
            ordersTracker.markManySeen([id, `${id}:completed`]);
          },
          markTransactionSeen: txTracker.markSeen,
          markTicketSeen: () => {},
          // Expose the bare-id set so existing OrdersTable highlighting
          // ("NEW" badge on the row) keeps working — strip any
          // `:completed` suffix before adding to the lookup set.
          unreadOrderIds: new Set(ordersTracker.unreadIds.map((s) => s.split(':')[0])),
          unreadTransactionIds: new Set(txTracker.unreadIds),
        }}>
        {activePage === 'new-order' && <NewOrderWithPricing user={user} token={token} onOrderCreated={refreshUser} onNavigate={setActivePage} />}
        
        {activePage === 'dashboard' && <UserDashboard token={token} setActivePage={setActivePage} onOpenOrders={openOrders} />}

        {activePage === 'orders' && (
          <UserOrders
            key={`uo-${editingOrder?.id || 'idle'}`}
            token={token}
            initialStatusFilter={ordersInitialStatus}
            initialOpenOrderId={ordersInitialOpenId}
            onOpenedOrderConsumed={() => setOrdersInitialOpenId('')}
            onEditOrder={(o) => setEditingOrder(o)}
          />
        )}
        {activePage === 'tickets' && <TicketsList token={token} isAdmin={false} />}

        {activePage === 'history' && <FileHistory token={token} />}

        {activePage === 'recharge' && <RechargeAccount token={token} onRecharged={refreshUser} onNavigate={setActivePage} />}
        {activePage === 'transactions' && (
          <UserTransactions
            token={token}
            onOpenOrder={(orderId) => {
              setOrdersInitialOpenId(orderId);
              setActivePage('orders');
            }}
          />
        )}
        {activePage === 'evc' && <EVCDashboard token={token} onChargedWallet={refreshUser} onNavigate={setActivePage} refreshEvcBalance={refreshEvcBalance} />}
        {activePage === 'original-files' && <OriginalFilesPage token={token} onChargedWallet={refreshUser} onNavigate={setActivePage} />}
        {activePage === 'profile' && <MyProfile token={token} user={user} onSaved={refreshUser} />}
        {activePage === 'news' && <NewsPage token={token} />}
        </UnreadBadgeProvider>
      </div>

      <Toaster position="top-right" richColors closeButton expand />

      {/* Welcome registration bonus — fires once for fresh signups. The
          modal self-checks via the API and renders nothing for migrated
          users, password-reset flows, or anyone who already dismissed it. */}
      <WelcomeBonusModal token={token} onRecharge={() => setActivePage('recharge')} />

      {/* Edit-order overlay — full-screen, replaces the visible page. We
          intentionally sit ABOVE the sticky header (z-60 vs header z-50)
          so the rose "Editing order" banner and Cancel button aren't
          obscured. Inner container keeps scroll behavior. */}
      {editingOrder && (
        <div className="fixed inset-0 z-[60] bg-slate-50 overflow-y-auto" data-testid="edit-order-overlay">
          <NewOrderWithPricing
            user={user}
            token={token}
            editingOrder={editingOrder}
            onCloseEdit={({ saved }) => {
              setEditingOrder(null);
              if (saved) refreshUser?.();
            }}
            onOrderCreated={refreshUser}
          />
        </div>
      )}
    </div>
  );
}
